import React, { useState } from 'react';
import { FaCopy, FaCheck, FaExternalLinkAlt } from 'react-icons/fa';
import { ProvenanceEntry } from '@/redux/slices/profileSlice';
import { formatPrice } from '@/utils/formatPrice';
import { formatFullDate } from '@/utils/formatTime';
import './ProvenanceTimeline.scss';

interface ProvenanceTimelineProps {
  entries: ProvenanceEntry[];
}

const EXPLORER_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://kleverscan.org/transaction'
    : 'https://testnet.kleverscan.org/transaction';

function truncateWallet(wallet: string): string {
  if (wallet.length <= 12) return wallet;
  return `${wallet.slice(0, 6)}…${wallet.slice(-4)}`;
}

const CopyableWallet: React.FC<{ wallet: string }> = ({ wallet }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(wallet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button className="provenance-wallet" onClick={handleCopy} title={wallet}>
      <span className="provenance-wallet-addr">{truncateWallet(wallet)}</span>
      {copied ? <FaCheck className="provenance-copy-icon copied" /> : <FaCopy className="provenance-copy-icon" />}
    </button>
  );
};

const ProvenanceTimeline: React.FC<ProvenanceTimelineProps> = ({ entries }) => {
  if (entries.length === 0) {
    return (
      <div className="provenance-timeline provenance-timeline--empty">
        <span className="provenance-label">Ownership History</span>
        <span className="provenance-no-data">No on-chain history yet</span>
      </div>
    );
  }

  return (
    <div className="provenance-timeline">
      <span className="provenance-label">Ownership History</span>
      <ol className="provenance-list">
        {entries.map((entry, i) => {
          const isFirst = i === 0;
          const isLast = i === entries.length - 1;
          return (
            <li key={entry.txHash} className={`provenance-item${isLast ? ' provenance-item--last' : ''}`}>
              <div className="provenance-dot-col">
                <div className={`provenance-dot${isFirst ? ' provenance-dot--origin' : ''}`} />
                {!isLast && <div className="provenance-line" />}
              </div>
              <div className="provenance-content">
                <div className="provenance-event">
                  {entry.txType === 'create' ? (
                    <span className="provenance-badge provenance-badge--mint">Minted</span>
                  ) : (
                    <span className="provenance-badge provenance-badge--sale">Sold</span>
                  )}
                  {entry.priceKlv !== null && (
                    <span className="provenance-price">{formatPrice(entry.priceKlv)}</span>
                  )}
                </div>
                <div className="provenance-wallets">
                  {entry.txType === 'create' ? (
                    <>
                      <span className="provenance-role">Creator</span>
                      <CopyableWallet wallet={entry.fromWallet} />
                    </>
                  ) : (
                    <>
                      <span className="provenance-role">From</span>
                      <CopyableWallet wallet={entry.fromWallet} />
                      {entry.toWallet && (
                        <>
                          <span className="provenance-role">To</span>
                          <CopyableWallet wallet={entry.toWallet} />
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="provenance-meta">
                  <span className="provenance-date">{formatFullDate(entry.date)}</span>
                  <a
                    href={`${EXPLORER_BASE}/${entry.txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="provenance-explorer"
                    title="View on Klever Explorer"
                  >
                    <FaExternalLinkAlt />
                  </a>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ProvenanceTimeline;
